<template>
  <div class="auth-shell d-flex justify-content-center align-items-center">
    <div class="card p-4 auth-card">
      <h2 class="title text-center mb-3">{{ $t('auth.reset.title') }}</h2>

      <form @submit.prevent="submit" novalidate>
        <!-- New password -->
        <div class="mb-3">
          <label for="newpass" class="form-label">{{ $t('auth.password.new') }}</label>
          <input
            v-model="form.new"
            type="password"
            id="newpass"
            class="form-control"
            required
          />
        </div>

        <!-- Rule-based feedback -->
        <div v-if="form.new && passwordErrors.length" class="text-danger mb-3">
          <ul class="mb-0"><li v-for="err in passwordErrors" :key="err">{{ err }}</li></ul>
        </div>

        <!-- Generic entropy warning -->
        <div v-else-if="form.new && !isStrong" class="text-danger mb-3">
          {{ $t('auth.password.tooWeakHint') }}
        </div>

        <!-- Confirm -->
        <div class="mb-3">
          <label for="confirm" class="form-label">{{ $t('auth.password.confirmNew') }}</label>
          <input
            v-model="form.confirm"
            type="password"
            id="confirm"
            class="form-control"
            required
          />
        </div>

        <!-- API error -->
        <div v-if="error" class="text-danger mb-3">{{ error }}</div>

        <!-- Submit -->
        <button type="submit" class="stora-btn-primary w-100" :disabled="loading || !isStrong">
          <span>{{ loading ? $t('auth.reset.resetting') : $t('auth.reset.submit') }}</span>
        </button>

        <!-- Success -->
        <div v-if="success" class="text-success mt-3">
          {{ $t('auth.reset.success') }}
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useUserStore } from '@/stores/UserStore'
import { getPasswordErrors, isPasswordStrong } from '@/utils/passwordPolicy'

export default {
  name: 'ResetPassword',
  setup() {
    const route = useRoute()
    const router = useRouter()
    const userStore = useUserStore()
    const { t } = useI18n()

    const token   = ref('')
    const form    = ref({ new: '', confirm: '' })
    const error   = ref(null)
    const success = ref(false)
    const loading = ref(false)

    onMounted(() => {
      token.value = route.query.token || ''
      if (!token.value) {
        error.value = t('auth.reset.noToken')
      }
    })

    const passwordErrors = computed(() =>
      getPasswordErrors(form.value.new)
    )
    const isStrong = computed(() =>
      isPasswordStrong(form.value.new)
    )

    const submit = async () => {
      error.value = null

      if (form.value.new !== form.value.confirm) {
        error.value = t('auth.password.mismatch')
        return
      }
      if (!isStrong.value) {
        error.value = t('auth.password.tooWeak')
        return
      }

      loading.value = true
      try {
        await userStore.resetPassword({
          token: token.value,
          newPassword: form.value.new
        })
        success.value = true
        setTimeout(() => router.push('/login'), 2000)
      } catch (err) {
        error.value = err.message || t('auth.reset.failed')
      } finally {
        loading.value = false
      }
    }

    return {
      form, error, success, loading,
      passwordErrors, isStrong, submit
    }
  }
}
</script>

<style scoped>
/* Layout to match login/change */
.auth-shell { min-height: 100vh; padding: 24px; }
.auth-card  { width: 100%; max-width: 480px; }
.title      { color:#425461; font-weight:700; }

/* Primary action (brand) */
.stora-btn-primary{
  display:inline-flex; align-items:center; justify-content:center;
  gap:8px; padding:10px 16px; border-radius:8px;
  background:#4F7EB3; color:#fff; border:0; cursor:pointer;
  transition: background .2s ease, transform .02s ease;
}
.stora-btn-primary:hover{ background:#B2C149; }
.stora-btn-primary:disabled{ opacity:.7; cursor:not-allowed; }

/* Keep input width tidy like login */
.form-control{ max-width: 500px; }
</style>